import { useState, useEffect, useRef, useCallback } from 'react';
import { Sidebar, Menu, MenuItem, sidebarClasses, menuClasses, SubMenu } from 'react-pro-sidebar';
import { NavLink, useLocation } from 'react-router-dom';
import {
    KeyboardDoubleArrowLeft,
    Dashboard,
    Store,
    Storefront,
    Devices,
    Redeem,
    GridView,
    WorkHistory,
    SettingsSuggest,
    RoomPreferences,
    ManageAccounts,
    HolidayVillage,
    ViewComfyAlt,
    SmartScreen,
    CastConnected,
    AdminPanelSettings,
    BackupTable,
} from '@mui/icons-material';
import styleVariables from '@/styles/style.module.scss';
import BaseIconButton from '../components/ui/BaseIconButton';
import logo from '../assets/images/AURA_logo.png';
import '../styles/_variables.scss';
import '@/styles/layouts/AppSidebar.scss';
import { useAppContext } from '../provider/contexts';
import { withPermission } from '@/utils/permissionUtils';
import { Box } from '@mui/material';
//import { fetchProjectInfoAPI } from '@/api/projectInfo';
import SettingsIcon from '@mui/icons-material/Settings';
import { getEnv } from '@/utils/env';
import { useTranslation } from 'react-i18next';

interface Props {
    isToggled: boolean;
    setIsToggled: (isToggled: boolean) => void;
    disabled?: boolean;
}

const ProtectedMenuItem = withPermission(MenuItem);
const ProtectedSubMenu = withPermission(SubMenu);

function WebSidebar({ isToggled, setIsToggled, disabled = false }: Props) {
    const { t } = useTranslation('common');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { pathname } = useLocation();
    const isInStoreMgmt = pathname.startsWith('/store_management');
    const isInAdmin = pathname.startsWith('/admin');
    const [isStoresMgmtOpen, setIsStoresMgmtOpen] = useState(isInStoreMgmt);
    const [isAdminOpen, setIsAdminOpen] = useState(isInAdmin);
    const prevPathRef = useRef(pathname);
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const submenuTransitionMs = 300;
    const sidebarTransitionMs = 400;
    const storeMgmtOpenRef = useRef(isInStoreMgmt);
    const adminOpenRef = useRef(isInAdmin);

    const setStoreMgmtOpen = useCallback((open: boolean, remember = true) => {
        if (remember) storeMgmtOpenRef.current = open;
        setIsStoresMgmtOpen(open);
    }, []);

    const setAdminOpenState = useCallback((open: boolean, remember = true) => {
        if (remember) adminOpenRef.current = open;
        setIsAdminOpen(open);
    }, []);

    const handleStoreMgmtOpenChange = useCallback(
        (open: boolean) => {
            setStoreMgmtOpen(open);
        },
        [setStoreMgmtOpen],
    );

    const handleAdminOpenChange = useCallback(
        (open: boolean) => {
            setAdminOpenState(open);
        },
        [setAdminOpenState],
    );

    const clearTimers = useCallback(() => {
        if (collapseTimerRef.current) {
            clearTimeout(collapseTimerRef.current);
            collapseTimerRef.current = null;
        }
        if (expandTimerRef.current) {
            clearTimeout(expandTimerRef.current);
            expandTimerRef.current = null;
        }
    }, []);

    const version = getEnv('AURA_UI_VERSION', '');

    // const handleStoreMgmtOpen = () => {
    //     setIsStoresMgmtOpen(!isStoresMgmtOpen);
    // };

    useEffect(() => {
        const wasInStoreMgmt = prevPathRef.current.startsWith('/store_management');
        const nowInStoreMgmt = pathname.startsWith('/store_management');
        const wasInAdmin = prevPathRef.current.startsWith('/admin');
        const nowInAdmin = pathname.startsWith('/admin');

        if (!isCollapsed) {
            if (!wasInStoreMgmt && nowInStoreMgmt) {
                setStoreMgmtOpen(true);
            } else if (wasInStoreMgmt && !nowInStoreMgmt) {
                setStoreMgmtOpen(false);
            }

            if (!wasInAdmin && nowInAdmin) {
                setAdminOpenState(true);
            } else if (wasInAdmin && !nowInAdmin) {
                setAdminOpenState(false);
            }
        } else {
            if (!wasInStoreMgmt && nowInStoreMgmt) {
                storeMgmtOpenRef.current = true;
            } else if (wasInStoreMgmt && !nowInStoreMgmt) {
                storeMgmtOpenRef.current = false;
            }

            if (!wasInAdmin && nowInAdmin) {
                adminOpenRef.current = true;
            } else if (wasInAdmin && !nowInAdmin) {
                adminOpenRef.current = false;
            }
        }

        prevPathRef.current = pathname;
    }, [pathname, isCollapsed, setStoreMgmtOpen, setAdminOpenState]);

    useEffect(() => {
        if (isToggled) {
            clearTimers();
            setIsCollapsed(false);
            setIsStoresMgmtOpen(storeMgmtOpenRef.current);
            setIsAdminOpen(adminOpenRef.current);
        }
    }, [isToggled, clearTimers]);

    const handleCollapseToggle = useCallback(() => {
        if (!isCollapsed) {
            clearTimers();
            storeMgmtOpenRef.current = isStoresMgmtOpen;
            adminOpenRef.current = isAdminOpen;
            setIsStoresMgmtOpen(false);
            setIsAdminOpen(false);
            if (isStoresMgmtOpen || isAdminOpen) {
                collapseTimerRef.current = setTimeout(() => {
                    setIsCollapsed(true);
                }, submenuTransitionMs);
            } else {
                setIsCollapsed(true);
            }
        } else {
            clearTimers();
            setIsCollapsed(false);
            expandTimerRef.current = setTimeout(() => {
                setIsStoresMgmtOpen(storeMgmtOpenRef.current);
                setIsAdminOpen(adminOpenRef.current);
            }, sidebarTransitionMs);
        }
    }, [isCollapsed, isStoresMgmtOpen, isAdminOpen, clearTimers]);

    useEffect(() => {
        return () => {
            clearTimers();
        };
    }, [clearTimers]);

    const { userDetails } = useAppContext();

    return disabled ? (
        <></>
    ) : (
        <div className="sidebar-area">
            <div className="sidebar-menu-wrapper">
                <div className="collapse-button">
                    <BaseIconButton className={`${isCollapsed ? 'collapsed' : ''}`} onClick={handleCollapseToggle}>
                        <KeyboardDoubleArrowLeft sx={{ fontSize: 18 }} />
                    </BaseIconButton>
                </div>
                <Sidebar
                    collapsed={isCollapsed}
                    toggled={isToggled}
                    breakPoint="md"
                    transitionDuration={400}
                    rootStyles={{
                        borderRight: 'none',
                        backgroundColor: '#333',
                        [`.${sidebarClasses.container}`]: {
                            backgroundColor: '#333',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        },
                    }}
                    onBackdropClick={() => {
                        setIsToggled(false);
                    }}
                >
                    <div>
                        <div className="sidebar-header">
                            <div className="logo">
                                <img src={logo} alt={t('AURA Logo')} />
                            </div>
                        </div>

                        <Menu
                            rootStyles={{
                                [`.${menuClasses.button}`]: {
                                    borderRadius: '7px',
                                    color: '#fff',
                                    margin: '5px 10px',
                                    paddingLeft: isCollapsed ? '11px' : '20px',
                                    pointerEvents: 'auto',
                                    '&:hover': {
                                        backgroundColor: `${styleVariables.secondaryColor}`,
                                        color: '#fff',
                                        [`.${menuClasses.icon}`]: {
                                            color: '#ffffff',
                                        },
                                    },
                                },
                                [`.${menuClasses.icon}`]: {
                                    color: `${styleVariables.primaryColor}`,
                                },
                                [`.${menuClasses.SubMenuExpandIcon}`]: {
                                    marginBottom: '4px',
                                },
                                [`.${menuClasses.button}.active`]: {
                                    backgroundColor: `${styleVariables.primaryColor}`,
                                    color: '#fff',
                                    [`.${menuClasses.icon}`]: {
                                        color: '#fff',
                                    },
                                },
                                [`.${menuClasses.subMenuContent}`]: {
                                    backgroundColor: `${styleVariables.darkGrey}`,
                                    paddingLeft: '20px',
                                },
                            }}
                        >
                            <ProtectedMenuItem
                                permission="page-dashboard-all-read"
                                component={<NavLink to="/dashboard" />}
                                icon={<Dashboard />}
                            >
                                {t('Dashboard')}
                            </ProtectedMenuItem>
                            <ProtectedMenuItem
                                permission="page-stores-all-read"
                                component={<NavLink to="/stores" />}
                                icon={<Store />}
                            >
                                {t('Stores')}
                            </ProtectedMenuItem>
                            <ProtectedSubMenu
                                permission={[
                                    'page-storeOverview-all-read',
                                    'page-storeProduct-all-read',
                                    'page-storeRegistration-all-read',
                                    'page-storeDevice-all-read',
                                    'page-storeHistory-all-read',
                                ]}
                                    label={t('Store Mgmt')}
                                icon={<Storefront />}
                                open={isStoresMgmtOpen}
                                onOpenChange={handleStoreMgmtOpenChange}
                            >
                                <ProtectedMenuItem
                                    permission="page-storeOverview-all-read"
                                    component={<NavLink to="/store_management/overview" />}
                                    icon={<GridView />}
                                >
                                    {t('Overview')}
                                </ProtectedMenuItem>
                                <ProtectedMenuItem
                                    permission="page-storeProduct-all-read"
                                    component={<NavLink to="/store_management/product" />}
                                    icon={<Redeem />}
                                >
                                    {t('Products')}
                                </ProtectedMenuItem>
                                <ProtectedMenuItem
                                    permission="page-storeDevice-all-read"
                                    component={<NavLink to="/store_management/device" />}
                                    icon={<SmartScreen />}
                                >
                                    {t('Devices')}
                                </ProtectedMenuItem>
                                <ProtectedMenuItem
                                    permission="page-storeRegistration-all-read"
                                    component={<NavLink to="/store_management/registration" />}
                                    icon={<CastConnected />}
                                >
                                    {t('Register Device')}
                                </ProtectedMenuItem>
                                <ProtectedMenuItem
                                    permission="page-storeHistory-all-read"
                                    component={<NavLink to="/store_management/history" />}
                                    icon={<WorkHistory />}
                                >
                                    {t('Activity Log')}
                                </ProtectedMenuItem>
                                <ProtectedMenuItem
                                    permission="page-storelayout-all-read"
                                    component={<NavLink to="/store_management/layout" />}
                                    icon={<ViewComfyAlt />}
                                >
                                    {t('Layout')}
                                </ProtectedMenuItem>
                                {userDetails?.user_level && userDetails.user_level! > 1 ? (
                                    <MenuItem
                                        component={<NavLink to="/store_management/settings" />}
                                        icon={<SettingsIcon />}
                                    >
                                        {t('Settings')}
                                    </MenuItem>
                                ) : null}
                            </ProtectedSubMenu>
                            <ProtectedMenuItem
                                permission="page-devices-all-read"
                                component={<NavLink to="/devices" />}
                                icon={<Devices />}
                            >
                                {t('Devices')}
                            </ProtectedMenuItem>
                            {userDetails?.user_level && userDetails.user_level > 1 ? (
                                <SubMenu
                                    label={t('Corporation')}
                                    icon={<SettingsSuggest />}
                                    open={isAdminOpen}
                                    onOpenChange={handleAdminOpenChange}
                                >
                                    <MenuItem component={<NavLink to="/admin/store" />} icon={<RoomPreferences />}>
                                        {t('Store')}
                                    </MenuItem>
                                    <MenuItem component={<NavLink to="/admin/layouts" />} icon={<BackupTable />}>
                                        {t('Layouts')}
                                    </MenuItem>
                                    <MenuItem component={<NavLink to="/admin/role" />} icon={<AdminPanelSettings />}>
                                        {t('Role')}
                                    </MenuItem>
                                    <MenuItem component={<NavLink to="/admin/account" />} icon={<ManageAccounts />}>
                                        {t('Account')}
                                    </MenuItem>
                                    <MenuItem component={<NavLink to="/admin/cluster" />} icon={<HolidayVillage />}>
                                        {t('Cluster')}
                                    </MenuItem>
                                    <MenuItem component={<NavLink to="/admin/settings" />} icon={<SettingsIcon />}>
                                        {t('Settings')}
                                    </MenuItem>
                                </SubMenu>
                            ) : (
                                <></>
                            )}
                        </Menu>
                    </div>
                    {/* Start | Footer for Mobile sidebar footer */}

                    <Box className="sidebar-footer-mobile">
                        <Box className="version-text">{version}</Box>
                        <Box className="link-wrapper">
                            <NavLink to="/credits" className="version-link">
                                {t('Credits')}
                            </NavLink>
                            <Box className="divider hidden">|</Box>
                            <NavLink to="/changelog" className="version-link hidden">
                                {t('Changelog')}
                            </NavLink>
                        </Box>
                    </Box>
                    {/* End | Footer for Mobile sidebar footer */}
                </Sidebar>
            </div>
            {/* Start | Footer for Desktop sidebar footer */}

            <Box className={`sidebar-footer-desktop ${isCollapsed ? 'collapsed' : ''}`}>
                <Box className="version-text">{version}</Box>
                <Box className="link-wrapper">
                    <NavLink to="/credits" className="version-link">
                        {t('Credits')}
                    </NavLink>
                    <Box className="divider hidden">|</Box>
                    <NavLink to="/changelog" className="version-link hidden">
                        {t('Changelog')}
                    </NavLink>
                </Box>
            </Box>

            {/* End | Footer for Desktop sidebar footer */}
        </div>
    );
}

export default WebSidebar;
